/**
 * Meta / Open Graph / canonical / JSON-LD para Frente 4.A (PDF consolidado).
 */

import { resolveMediaSrc } from "@/core/helpers/mediaSrc";

const SITE_NAME = "GoKey";
const DEFAULT_DESCRIPTION =
  "Encuentra seminuevos certificados de distribuidores Toyota. Cotiza y precalifícate en GoKey.";

function ensureMeta(attr: "name" | "property", key: string, content: string): void {
  if (typeof document === "undefined") return;
  const selector = attr === "name" ? `meta[name="${key}"]` : `meta[property="${key}"]`;
  let el = document.querySelector(selector) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function ensureLink(rel: string, href: string): void {
  if (typeof document === "undefined") return;
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

export function canonicalUrl(path?: string): string {
  if (typeof window === "undefined") {
    return path || "/";
  }
  const origin = window.location.origin;
  const p = path ?? window.location.pathname + window.location.search;
  const normalized = p.startsWith("/") ? p : `/${p}`;
  return `${origin}${normalized}`;
}

export type SeoMetaInput = {
  title?: string;
  description?: string;
  path?: string;
  image?: string | null;
  type?: string;
};

export function applySeoMeta(input: SeoMetaInput): void {
  const title = (input.title || SITE_NAME).trim();
  const description = (input.description || DEFAULT_DESCRIPTION).trim();
  const url = canonicalUrl(input.path);
  const image = input.image ? resolveMediaSrc(input.image) || undefined : undefined;
  const ogType = input.type || "website";

  document.title = title.includes(SITE_NAME) ? title : `${title} | ${SITE_NAME}`;

  ensureMeta("name", "description", description);
  ensureLink("canonical", url);

  ensureMeta("property", "og:site_name", SITE_NAME);
  ensureMeta("property", "og:type", ogType);
  ensureMeta("property", "og:title", document.title);
  ensureMeta("property", "og:description", description);
  ensureMeta("property", "og:url", url);
  if (image) {
    ensureMeta("property", "og:image", image);
  }

  ensureMeta("name", "twitter:card", image ? "summary_large_image" : "summary");
  ensureMeta("name", "twitter:title", document.title);
  ensureMeta("name", "twitter:description", description);
  if (image) {
    ensureMeta("name", "twitter:image", image);
  }
}

const JSON_LD_ID = "gokey-jsonld-vehicle";

export function clearVehicleJsonLd(): void {
  if (typeof document === "undefined") return;
  document.getElementById(JSON_LD_ID)?.remove();
}

export function setVehicleJsonLd(vehicle: {
  id?: string | number;
  brand?: string;
  model?: string;
  year?: string | number;
  price?: string | number;
  Description?: string;
  Mileage?: string | number;
  cover?: string | null;
  url?: string | null;
}): void {
  if (typeof document === "undefined") return;
  clearVehicleJsonLd();

  const name = [vehicle.brand, vehicle.model, vehicle.year].filter(Boolean).join(" ");
  const url = vehicle.url || canonicalUrl(`/detalle/${vehicle.id}`);
  const payload: Record<string, unknown> = {
    "@context": "https://schema.org",
    "@type": "Vehicle",
    name: name || "Vehículo seminuevo",
    brand: vehicle.brand ? { "@type": "Brand", name: vehicle.brand } : undefined,
    model: vehicle.model,
    vehicleModelDate: vehicle.year ? String(vehicle.year) : undefined,
    description: vehicle.Description || undefined,
    mileageFromOdometer: vehicle.Mileage
      ? {
          "@type": "QuantitativeValue",
          value: Number(vehicle.Mileage) || vehicle.Mileage,
          unitCode: "KMT",
        }
      : undefined,
    offers: vehicle.price
      ? {
          "@type": "Offer",
          priceCurrency: "MXN",
          price: Number(String(vehicle.price).replace(/[^\d.]/g, "")) || vehicle.price,
          availability: "https://schema.org/InStock",
          url,
        }
      : undefined,
    image: vehicle.cover
      ? resolveMediaSrc(vehicle.cover) || undefined
      : undefined,
    url,
  };

  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined) delete payload[k];
  });

  const script = document.createElement("script");
  script.type = "application/ld+json";
  script.id = JSON_LD_ID;
  script.textContent = JSON.stringify(payload);
  document.head.appendChild(script);
}
